import { Fragment } from 'preact';
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';

import { imageFileToDataUrl, imageFromTransfer } from '../capture/image';
import { t } from '../i18n';
import { captureCount, MAX_CAPTURES, removeBlock, safeHref } from '../story';
import { Icon, Spinner } from './icons';

import type { DraftBlock, InsertPoint } from '../story';
import type { Capture } from '../types';

export type CaptureTool = 'element' | 'area' | 'screenshot';

interface Props {
  blocks: DraftBlock[];
  onChange: (blocks: DraftBlock[]) => void;
  onCapture: (tool: CaptureTool, at: InsertPoint) => void;
  onCancelPicking: () => void;
  /** A pasted or dropped image, already converted to a data URL. */
  onImage: (at: InsertPoint, dataUrl: string) => void;
  /** Takes a failed screenshot again. */
  onRetry: (blockId: string) => void;
  retryingId: string | null;
  /** Block to focus (caret at the start) after a capture was inserted. */
  focusId: string | null;
  /** Where the capture in progress will land; the placeholder shows there. */
  insertAt: InsertPoint | null;
  picking: 'element' | 'area' | 'capturing' | null;
  activeTool: CaptureTool | null;
  placeholder: string;
}

type SelectionWithComposed = Selection & {
  getComposedRanges?: (options: { shadowRoots: ShadowRoot[] }) => StaticRange[];
};

/**
 * Reads the caret inside the shadow root. Chrome exposes shadowRoot.getSelection(); Firefox returns
 * shadow ranges from document.getSelection(); Safari needs getComposedRanges().
 */
function currentRange(el: HTMLElement): Range | null {
  const root = el.getRootNode() as ShadowRoot & { getSelection?: () => Selection | null };
  const selection = (root.getSelection?.() ??
    document.getSelection()) as SelectionWithComposed | null;
  if (!selection || selection.rangeCount === 0) return null;
  const range = selection.getRangeAt(0);
  if (el.contains(range.startContainer)) return range;
  try {
    const composed = selection.getComposedRanges?.({ shadowRoots: [root] })[0];
    if (composed && el.contains(composed.startContainer)) {
      const r = document.createRange();
      r.setStart(composed.startContainer, composed.startOffset);
      r.setEnd(composed.endContainer, composed.endOffset);
      return r;
    }
  } catch {
    // Older engines; fall through.
  }
  return null;
}

function restoreRange(el: HTMLElement, range: Range) {
  el.focus();
  const root = el.getRootNode() as ShadowRoot & { getSelection?: () => Selection | null };
  const selection = root.getSelection?.() ?? document.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(range);
}

function rangeHtml(range: Range): string {
  const container = document.createElement('div');
  container.append(range.cloneContents());
  return container.innerHTML;
}

const isBlank = (el: HTMLElement) => !(el.textContent ?? '').replace(/ /g, ' ').trim();

function TextBlock({
  block,
  placeholder,
  register,
  onInput,
  onCaret,
  onPasteImage,
}: {
  block: Extract<DraftBlock, { kind: 'text' }>;
  placeholder: string;
  register: (id: string, el: HTMLElement | null) => void;
  onInput: (id: string, html: string) => void;
  onCaret: (id: string) => void;
  onPasteImage: (file: File) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  // Uncontrolled: the HTML is set once, so re-renders never move the caret.
  useLayoutEffect(() => {
    const el = ref.current!;
    el.innerHTML = block.html;
    el.dataset.empty = String(isBlank(el));
    register(block.id, el);
    return () => register(block.id, null);
  }, [block.id]);

  return (
    <div
      ref={ref}
      class="hfb-story-text"
      contentEditable
      role="textbox"
      aria-multiline="true"
      aria-label={t().story}
      data-placeholder={placeholder}
      onInput={(event) => {
        const el = event.currentTarget;
        el.dataset.empty = String(isBlank(el));
        onInput(block.id, el.innerHTML);
      }}
      onKeyUp={() => onCaret(block.id)}
      onMouseUp={() => onCaret(block.id)}
      onFocus={() => onCaret(block.id)}
      onPaste={(event) => {
        const image = imageFromTransfer(event.clipboardData);
        if (image) {
          event.preventDefault();
          onCaret(block.id);
          onPasteImage(image);
          return;
        }
        // Plain text only, so formatting and markup from other pages stay out.
        event.preventDefault();
        const text = event.clipboardData?.getData('text/plain') ?? '';
        document.execCommand('insertText', false, text);
      }}
    />
  );
}

function captureLabel(capture: Capture): { kind: string; detail: string } {
  const s = t();
  const sel = capture.selection;
  if (capture.pasted) return { kind: s.pastedImage, detail: '' };
  if (!sel) return { kind: s.screenshot, detail: '' };
  if (sel.kind === 'element') return { kind: s.elementLabel, detail: sel.description };
  return {
    kind: s.areaLabel,
    detail: `${Math.round(sel.rect.width)} × ${Math.round(sel.rect.height)} px`,
  };
}

function CaptureCard({
  capture,
  onRemove,
  onView,
  onRetry,
  retrying,
}: {
  capture: Capture;
  onRemove: () => void;
  onView: (src: string) => void;
  onRetry: () => void;
  retrying: boolean;
}) {
  const s = t();
  const label = captureLabel(capture);
  const icon = capture.pasted
    ? 'image'
    : capture.selection?.kind === 'area'
      ? 'area'
      : capture.selection
        ? 'element'
        : 'camera';
  if (retrying) {
    return (
      <div class="hfb-pending is-capturing" contentEditable={false} role="status">
        <span class="hfb-pending-shot">
          <Spinner />
          {s.takingScreenshot}
        </span>
        <span class="hfb-pending-caption" />
      </div>
    );
  }
  const failed = !capture.screenshot;
  return (
    <figure class={`hfb-capture${failed ? ' is-failed' : ''}`} contentEditable={false}>
      {failed ? (
        <div class="hfb-capture-failed">{s.captureFailed}</div>
      ) : (
        <button
          type="button"
          class="hfb-capture-view"
          data-hint={s.viewLarger}
          aria-label={`${s.viewLarger}: ${label.kind} ${label.detail}`}
          onClick={() => onView(capture.screenshot!)}
        >
          <img src={capture.screenshot!} alt="" />
        </button>
      )}
      <figcaption>
        <Icon name={icon} />
        <span class="hfb-capture-label">
          {label.kind} {label.detail && <b>{label.detail}</b>}
        </span>
        {failed && !capture.pasted && capture.selection ? (
          <button type="button" class="hfb-capture-retry" onClick={onRetry}>
            {s.retry}
          </button>
        ) : (
          <button type="button" class="hfb-capture-remove" onClick={onRemove}>
            <Icon name="x" size={14} />
            {s.remove}
          </button>
        )}
      </figcaption>
    </figure>
  );
}

/** Where the capture in progress will land, so the tester sees it is coming and can cancel. */
function PendingCapture({
  picking,
  onCancel,
}: {
  picking: 'element' | 'area' | 'capturing';
  onCancel: () => void;
}) {
  const s = t();
  if (picking === 'capturing') {
    return (
      <div class="hfb-pending is-capturing" contentEditable={false} role="status">
        <span class="hfb-pending-shot">
          <Spinner />
          {s.takingScreenshot}
        </span>
        <span class="hfb-pending-caption" />
      </div>
    );
  }
  return (
    <div class="hfb-pending" contentEditable={false} role="status">
      <Icon name="target" />
      <span>
        <strong>{picking === 'element' ? s.pickElementInline : s.pickAreaInline}</strong>{' '}
        {s.landsHere}
      </span>
      <button type="button" class="hfb-btn hfb-btn-outline hfb-btn-small" onClick={onCancel}>
        {s.cancel}
      </button>
    </div>
  );
}

const TOOLS: Array<{ tool: CaptureTool; icon: 'element' | 'area' | 'camera' }> = [
  { tool: 'element', icon: 'element' },
  { tool: 'area', icon: 'area' },
  { tool: 'screenshot', icon: 'camera' },
];

export function StoryEditor(props: Props) {
  const { blocks, onChange, onCapture, onImage, focusId, picking, placeholder } = props;
  const s = t();
  const elements = useRef(new Map<string, HTMLElement>());
  const caret = useRef<{ id: string; range: Range } | null>(null);
  const blocksRef = useRef(blocks);
  blocksRef.current = blocks;
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [dragging, setDragging] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  /** Screenshot shown full size; Esc or a click closes it. */
  const [viewing, setViewing] = useState<string | null>(null);
  useEffect(() => {
    if (!viewing) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setViewing(null);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [viewing]);
  const linkInput = useRef<HTMLInputElement>(null);
  const dragDepth = useRef(0);

  const register = (id: string, el: HTMLElement | null) => {
    if (el) elements.current.set(id, el);
    else elements.current.delete(id);
  };

  const rememberCaret = (id: string) => {
    const el = elements.current.get(id);
    const range = el ? currentRange(el) : null;
    if (range) caret.current = { id, range: range.cloneRange() };
  };

  const updateHtml = (id: string, html: string) => {
    onChange(blocksRef.current.map((b) => (b.id === id && b.kind === 'text' ? { ...b, html } : b)));
  };

  useEffect(() => {
    if (!focusId) return;
    const el = elements.current.get(focusId);
    if (!el) return;
    const range = document.createRange();
    range.setStart(el, 0);
    range.collapse(true);
    restoreRange(el, range);
    caret.current = { id: focusId, range };
  }, [focusId]);

  useLayoutEffect(() => {
    if (linkOpen) linkInput.current?.focus();
  }, [linkOpen]);

  const syncFromDom = () => {
    const id = caret.current?.id;
    const el = id ? elements.current.get(id) : undefined;
    if (id && el) {
      el.dataset.empty = String(isBlank(el));
      updateHtml(id, el.innerHTML);
    }
  };

  const format = (command: 'bold' | 'italic') => {
    document.execCommand(command);
    syncFromDom();
  };

  const openLink = () => {
    const id = caret.current?.id;
    if (id && elements.current.get(id)) rememberCaret(id);
    setLinkUrl('');
    setLinkOpen(true);
  };

  /** Esc or ×: close the field and put the caret back where it was. */
  const closeLink = () => {
    setLinkOpen(false);
    const saved = caret.current;
    const el = saved ? elements.current.get(saved.id) : undefined;
    if (saved && el) restoreRange(el, saved.range);
  };

  const applyLink = () => {
    const href = safeHref(linkUrl);
    const saved = caret.current;
    const el = saved ? elements.current.get(saved.id) : undefined;
    setLinkOpen(false);
    if (!href || !saved || !el) return;
    restoreRange(el, saved.range);
    if (saved.range.collapsed) {
      const a = document.createElement('a');
      a.href = href;
      a.textContent = linkUrl.trim();
      document.execCommand('insertHTML', false, a.outerHTML);
    } else {
      document.execCommand('createLink', false, href);
    }
    syncFromDom();
  };

  /** Where an image goes: the caret's text block, split around the caret. */
  const insertPointAtCaret = (): InsertPoint => {
    const current = blocksRef.current;
    const lastText = current.findLast((b) => b.kind === 'text');
    const id = caret.current?.id ?? lastText?.id ?? null;
    const el = id ? elements.current.get(id) : undefined;
    const block = current.find((b) => b.id === id);
    if (!el || !block || block.kind !== 'text')
      return { blockId: null, sourceHtml: '', before: '', after: '' };
    const range = caret.current?.id === id ? caret.current.range : null;
    let before = el.innerHTML;
    let after = '';
    if (range && el.contains(range.startContainer)) {
      const head = document.createRange();
      head.selectNodeContents(el);
      head.setEnd(range.startContainer, range.startOffset);
      const tail = document.createRange();
      tail.selectNodeContents(el);
      tail.setStart(range.endContainer, range.endOffset);
      before = rangeHtml(head);
      after = rangeHtml(tail);
    }
    return { blockId: id, sourceHtml: block.html, before, after };
  };

  const count = captureCount(blocks);
  const full = count >= MAX_CAPTURES;

  const addImage = async (file: File) => {
    if (captureCount(blocksRef.current) >= MAX_CAPTURES) return;
    const at = insertPointAtCaret();
    try {
      onImage(at, await imageFileToDataUrl(file));
      setImageError(null);
    } catch {
      setImageError(s.imageUnreadable);
    }
  };

  // Buttons must not take focus from the text, or formatting would lose the selection.
  const keepFocus = (event: MouseEvent) => event.preventDefault();
  const busy = picking !== null;
  const pendingAfter = busy ? (props.insertAt?.blockId ?? blocks.at(-1)?.id ?? null) : null;

  return (
    <>
      <div
        class={`hfb-story${dragging ? ' is-dragging' : ''}`}
        onDragEnter={(event) => {
          if (!event.dataTransfer?.types.includes('Files') || full) return;
          dragDepth.current += 1;
          setDragging(true);
        }}
        onDragLeave={() => {
          dragDepth.current = Math.max(0, dragDepth.current - 1);
          if (dragDepth.current === 0) setDragging(false);
        }}
        onDragOver={(event) => {
          if (event.dataTransfer?.types.includes('Files')) event.preventDefault();
        }}
        onDrop={(event) => {
          dragDepth.current = 0;
          setDragging(false);
          const image = imageFromTransfer(event.dataTransfer);
          if (!image) return;
          event.preventDefault();
          void addImage(image);
        }}
      >
        <div
          class="hfb-story-toolbar"
          role="toolbar"
          aria-label={`${s.bold}, ${s.italic}, ${s.link}`}
        >
          <button
            type="button"
            class="hfb-tool"
            title={`${s.bold} (⌘B)`}
            aria-label={s.bold}
            onMouseDown={keepFocus}
            onClick={() => format('bold')}
          >
            <b>B</b>
          </button>
          <button
            type="button"
            class="hfb-tool"
            title={`${s.italic} (⌘I)`}
            aria-label={s.italic}
            onMouseDown={keepFocus}
            onClick={() => format('italic')}
          >
            <i>I</i>
          </button>
          <button
            type="button"
            class={`hfb-tool${linkOpen ? ' is-active' : ''}`}
            title={`${s.link} (⌘K)`}
            aria-label={s.link}
            aria-expanded={linkOpen}
            onMouseDown={keepFocus}
            onClick={() => (linkOpen ? closeLink() : openLink())}
          >
            <Icon name="link" />
          </button>
        </div>

        {linkOpen && (
          // Not a <form>: this sits inside the feedback form, and nested forms submit the outer one.
          <div class="hfb-linkbar">
            <input
              ref={linkInput}
              class="hfb-input"
              type="url"
              inputMode="url"
              placeholder="https://"
              aria-label={s.link}
              value={linkUrl}
              onInput={(e) => setLinkUrl(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  applyLink();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  e.stopPropagation();
                  closeLink();
                }
              }}
            />
            <button type="button" class="hfb-btn hfb-btn-primary hfb-btn-small" onClick={applyLink}>
              {s.linkAdd}
            </button>
            <button type="button" class="hfb-icon-btn" aria-label={s.linkClose} onClick={closeLink}>
              <Icon name="x" />
            </button>
          </div>
        )}

        <div
          class="hfb-story-body"
          onKeyDown={(event) => {
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
              event.preventDefault();
              openLink();
            }
          }}
        >
          {blocks.map((block, index) => (
            <Fragment key={block.id}>
              {block.kind === 'text' ? (
                <TextBlock
                  block={block}
                  placeholder={index === 0 ? placeholder : s.continueStory}
                  register={register}
                  onInput={updateHtml}
                  onCaret={rememberCaret}
                  onPasteImage={(file) => void addImage(file)}
                />
              ) : (
                <CaptureCard
                  capture={block.capture}
                  onView={setViewing}
                  onRetry={() => props.onRetry(block.id)}
                  retrying={props.retryingId === block.id}
                  onRemove={() => onChange(removeBlock(blocksRef.current, block.id))}
                />
              )}
              {picking && block.id === pendingAfter && (
                <PendingCapture picking={picking} onCancel={props.onCancelPicking} />
              )}
            </Fragment>
          ))}
        </div>

        <div class="hfb-story-tools">
          {TOOLS.map(({ tool, icon }) => (
            <button
              key={tool}
              type="button"
              class={`hfb-tool hfb-tool-capture${props.activeTool === tool ? ' is-active' : ''}`}
              disabled={full || (busy && props.activeTool !== tool)}
              aria-pressed={props.activeTool === tool}
              onMouseDown={keepFocus}
              onClick={() =>
                props.activeTool === tool
                  ? props.onCancelPicking()
                  : onCapture(tool, insertPointAtCaret())
              }
            >
              <Icon name={icon} />
              <span>
                {tool === 'element' ? s.element : tool === 'area' ? s.area : s.screenshot}
              </span>
            </button>
          ))}
          <span
            class={`hfb-counter${full ? ' is-full' : ''}`}
            aria-label={`${count} / ${MAX_CAPTURES}`}
          >
            {count} / {MAX_CAPTURES}
          </span>
        </div>

        {dragging && (
          <div class="hfb-dropzone" aria-hidden="true">
            <Icon name="image" size={26} />
            <strong>{s.dropTitle}</strong>
            <span>{s.dropSub(MAX_CAPTURES - count - 1, MAX_CAPTURES)}</span>
          </div>
        )}
      </div>
      <p class={`hfb-hint${full ? ' is-strong' : ''}`}>
        {linkOpen ? s.linkHint : full ? s.limitReached(MAX_CAPTURES) : s.storyHint}
      </p>
      {viewing && (
        <div
          class="hfb-lightbox"
          role="dialog"
          aria-label={s.screenshot}
          onClick={() => setViewing(null)}
        >
          <img src={viewing} alt="" />
          <button type="button" class="hfb-icon-btn" aria-label={s.closeImage} autoFocus>
            <Icon name="x" size={20} />
          </button>
        </div>
      )}
      {imageError && <p class="hfb-field-error">{imageError}</p>}
    </>
  );
}
